import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Sparkline } from './sparkline';
import { cn } from '@/lib/utils';

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-card border border-line bg-white shadow-card', className)}>
      {children}
    </div>
  );
}

export function CardHeader({
  titre,
  description,
  action,
  className,
}: {
  titre: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-ink">{titre}</h2>
        {description ? <p className="mt-0.5 text-xs text-ink-muted">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardContent({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('p-4', className)}>{children}</div>;
}

/**
 * Variation d'une mesure d'une période à l'autre.
 *
 * `variation` est un rapport (`0,12` pour +12 %), et `null` quand la
 * comparaison n'a pas de sens — période précédente à zéro, où toute hausse
 * serait « infinie ». Mieux vaut ne rien afficher qu'un pourcentage faux.
 *
 * `inverse` retourne le code couleur : sur un reste dû ou une valeur de stock
 * immobilisée, une hausse n'est pas une bonne nouvelle.
 */
export function Tendance({
  variation,
  libelle,
  inverse,
  precision,
}: {
  variation: number | null;
  /** À quoi on compare, ex. « vs août 26 ». */
  libelle?: string;
  inverse?: boolean;
  /** Infobulle : ce que la comparaison couvre exactement. */
  precision?: string;
}) {
  if (variation === null || !Number.isFinite(variation)) return null;

  // En deçà d'un point de pourcentage, annoncer un sens serait du bruit.
  const stable = Math.abs(variation) < 0.01;
  const hausse = variation > 0;
  const favorable = inverse ? !hausse : hausse;

  const Fleche = stable ? Minus : hausse ? ArrowUpRight : ArrowDownRight;
  const pourcentage = `${Math.abs(Math.round(variation * 100))} %`;

  return (
    <span className="inline-flex items-center gap-1.5" title={precision}>
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium tabular',
          stable && 'bg-surface-sunken text-ink-muted',
          !stable && favorable && 'bg-success-soft text-success',
          !stable && !favorable && 'bg-danger-soft text-danger',
        )}
      >
        <Fleche aria-hidden className="h-3 w-3" />
        {stable ? 'stable' : `${hausse ? '+' : '−'}${pourcentage}`}
      </span>
      {libelle ? <span className="truncate text-xs text-ink-muted">{libelle}</span> : null}
    </span>
  );
}

/** Jauge horizontale plate — une part sur un tout, jamais une valeur absolue. */
function Jauge({ part, libelle }: { part: number; libelle: ReactNode }) {
  const borne = Math.max(0, Math.min(1, part));

  return (
    <div className="mt-2">
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
        role="progressbar"
        aria-valuenow={Math.round(borne * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-mustard-500 transition-[width] duration-500"
          style={{ width: `${borne * 100}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-ink-muted tabular">{libelle}</p>
    </div>
  );
}

/**
 * Tuile de statistique du tableau de bord.
 *
 * Le libellé secondaire porte la seconde valeur d'une même mesure — la valeur
 * du stock s'affiche au prix d'achat et au prix de vente. `tendance`, `jauge`
 * et `sparkline` s'excluent en pratique : une tuile qui porte les trois ne se
 * lit plus d'un coup d'œil, et c'est tout ce qu'on lui demande.
 */
export function StatCard({
  libelle,
  valeur,
  secondaire,
  icone,
  accent,
  onClick,
  className,
  tendance,
  jauge,
  sparkline,
}: {
  libelle: string;
  valeur: ReactNode;
  secondaire?: ReactNode;
  icone?: ReactNode;
  accent?: boolean;
  onClick?: () => void;
  className?: string;
  tendance?: ReactNode;
  jauge?: { part: number; libelle: ReactNode };
  /** Historique mensuel, du plus ancien au plus récent. */
  sparkline?: number[];
}) {
  const contenu = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
          {libelle}
        </span>
        {icone ? <span className="text-mustard-600">{icone}</span> : null}
      </div>
      <p
        className={cn(
          'mt-2 text-2xl font-semibold tabular tracking-tight',
          accent && 'text-mustard-600',
        )}
      >
        {valeur}
      </p>
      {tendance ? <div className="mt-1.5 flex min-w-0">{tendance}</div> : null}
      {secondaire ? <p className="mt-1 text-xs text-ink-muted tabular">{secondaire}</p> : null}
      {jauge ? <Jauge part={jauge.part} libelle={jauge.libelle} /> : null}
      {sparkline ? (
        <div className="-mx-1 mt-3">
          <Sparkline valeurs={sparkline} titre={`${libelle} — douze derniers mois`} />
        </div>
      ) : null}
    </>
  );

  const classes = cn(
    'rounded-card border border-line bg-white p-4 text-left shadow-card',
    onClick &&
      'transition-colors hover:border-mustard-500 hover:bg-mustard-50 focus-visible:border-mustard-500',
    className,
  );

  return onClick ? (
    <button type="button" onClick={onClick} className={classes}>
      {contenu}
    </button>
  ) : (
    <div className={classes}>{contenu}</div>
  );
}
